import { useState, useMemo, useEffect } from 'react'
import { ChangelogFilter, ChangelogVersion } from '../types'

interface UseChangelogFilterOptions {
  data: ChangelogVersion[]
  admin?: boolean
}

interface UseChangelogFilterReturn {
  filter: ChangelogFilter
  searchTerm: string
  deferredSearchTerm: string
  filteredData: ChangelogVersion[]
  updateFilter: (filterKey: keyof ChangelogFilter) => void
  updateSearchTerm: (term: string) => void
  resetFilters: () => void
}

const initialFilter: ChangelogFilter = {
  improvement: false,
  fix: false,
  feature: false,
  admin: false,
}

export const useChangelogFilter = ({
  data,
  admin = false,
}: UseChangelogFilterOptions): UseChangelogFilterReturn => {
  const [filter, setFilter] = useState<ChangelogFilter>(initialFilter)
  const [searchTerm, setSearchTerm] = useState('')
  const [deferredSearchTerm, setDeferredSearchTerm] = useState('')

  // Use useEffect to defer search term updates for better performance
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDeferredSearchTerm(searchTerm)
    }, 300) // 300ms debounce

    return () => clearTimeout(timeoutId)
  }, [searchTerm])

  const updateFilter = (filterKey: keyof ChangelogFilter) => {
    setFilter((prev) => ({
      ...prev,
      [filterKey]: !prev[filterKey],
    }))
  }

  const updateSearchTerm = (term: string) => {
    setSearchTerm(term)
  }

  const resetFilters = () => {
    setFilter(initialFilter)
    setSearchTerm('')
  }

  const filteredData = useMemo(() => {
    if (!data) return []

    // Check if all filters are false (show all)
    const noFiltersActive =
      !filter.improvement && !filter.feature && !filter.fix && !filter.admin

    return data
      .map((version) => {
        // Helper function to filter items based on search term and admin status
        const filterItems = (items: any[] | undefined) => {
          if (!items) return items

          // Always filter out admin entries if admin prop is false
          let filteredItems = items
          if (!admin) {
            filteredItems = items.filter((item) => {
              if (typeof item === 'object' && item && 'admin' in item) {
                return !item.admin
              }
              return true
            })
          }

          // If admin filter is active, show only admin entries
          if (filter.admin && admin) {
            filteredItems = filteredItems.filter((item) => {
              if (typeof item === 'object' && item && 'admin' in item) {
                return item.admin
              }
              return false // Regular items are hidden when admin filter is active
            })
          }

          if (!deferredSearchTerm) return filteredItems

          const searchLower = deferredSearchTerm.toLowerCase().trim()
          const searchWords = searchLower.split(/\s+/)

          const matchesWholeWords = (text: string): boolean => {
            const textLower = text.toLowerCase()
            return searchWords.every((word) => {
              const regex = new RegExp(
                `\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
                'i',
              )
              return regex.test(textLower)
            })
          }

          return filteredItems.filter((item) => {
            if (typeof item === 'string') {
              return matchesWholeWords(item)
            } else if (item && typeof item === 'object' && 'message' in item) {
              return (
                typeof item.message === 'string' &&
                matchesWholeWords(item.message)
              )
            }
            return false
          })
        }

        // Filter items within each category if there's a search term
        const filteredFeatures = filterItems(version.features)
        const filteredImprovements = filterItems(version.improvements)
        const filteredFixes = filterItems(version.fixes)

        // For search: check if version/date matches or if any items match
        if (deferredSearchTerm) {
          const searchLower = deferredSearchTerm.toLowerCase().trim()
          const searchWords = searchLower.split(/\s+/)

          const matchesWholeWords = (text: string): boolean => {
            const textLower = text.toLowerCase()
            return searchWords.every((word) => {
              const regex = new RegExp(
                `\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`,
                'i',
              )
              return regex.test(textLower)
            })
          }

          const versionMatches = matchesWholeWords(version.version)
          const dateMatches = matchesWholeWords(version.date)
          const hasMatchingItems =
            (filteredFeatures && filteredFeatures.length > 0) ||
            (filteredImprovements && filteredImprovements.length > 0) ||
            (filteredFixes && filteredFixes.length > 0)

          // If nothing matches, skip this version entirely
          if (!versionMatches && !dateMatches && !hasMatchingItems) {
            return null
          }

          // Return version with only matching items (unless version/date matched, then show all)
          return {
            ...version,
            features:
              versionMatches || dateMatches
                ? filterItems(version.features)
                : filteredFeatures,
            improvements:
              versionMatches || dateMatches
                ? filterItems(version.improvements)
                : filteredImprovements,
            fixes:
              versionMatches || dateMatches
                ? filterItems(version.fixes)
                : filteredFixes,
          }
        }

        // Category filter (when no search term)
        if (!noFiltersActive) {
          const hasFeatures =
            filter.feature && filteredFeatures && filteredFeatures.length > 0
          const hasImprovements =
            filter.improvement &&
            filteredImprovements &&
            filteredImprovements.length > 0
          const hasFixes =
            filter.fix && filteredFixes && filteredFixes.length > 0
          const hasAdminItems =
            filter.admin &&
            admin &&
            ((filteredFeatures && filteredFeatures.length > 0) ||
              (filteredImprovements && filteredImprovements.length > 0) ||
              (filteredFixes && filteredFixes.length > 0))

          if (!hasFeatures && !hasImprovements && !hasFixes && !hasAdminItems) {
            return null
          }
        }

        return {
          ...version,
          features: filteredFeatures,
          improvements: filteredImprovements,
          fixes: filteredFixes,
        }
      })
      .filter((version) => {
        // Filter out null versions and versions that have no content after admin filtering
        if (!version) return false

        const hasContent =
          (version.features && version.features.length > 0) ||
          (version.improvements && version.improvements.length > 0) ||
          (version.fixes && version.fixes.length > 0)

        return Boolean(hasContent)
      }) as ChangelogVersion[]
  }, [data, filter, deferredSearchTerm, admin])

  return {
    filter,
    searchTerm,
    deferredSearchTerm,
    filteredData,
    updateFilter,
    updateSearchTerm,
    resetFilters,
  }
}
